// Afbeeldingen plakken of slepen in een mail-editor (helpdesk-antwoord, Nieuw
// ticket). Een schermafdruk uit het klembord is al snel 1 à 3 MB als PNG; als
// data-URL in de HTML wordt dat nog een derde groter en liep het bericht tegen
// de limieten van de server ("Het bericht is te lang", 30-10-2026). De browser
// verkleint de afbeelding daarom vóór ze in de editor komt:
//   • langste zijde hooguit MAX_ZIJDE px (een mail is toch maar ~600 px breed);
//   • blijft ze als PNG groter dan PNG_GRENS, dan wordt het JPEG (foto's); kleine
//     schermafdrukken met tekst blijven PNG, dat leest scherper.
// De server haalt de afbeeldingen daarna uit de HTML en stuurt ze als inline
// bijlage mee (backend/src/util/inlineAfbeeldingen.js).

export const MAX_ZIJDE = 1600;
export const PNG_GRENS = 600 * 1024;        // bytes
export const JPEG_KWALITEIT = 0.85;
export const MAX_PER_BEELD = 5 * 1024 * 1024;

// Nieuwe afmetingen: schalen tot de langste zijde ≤ max, nooit vergroten.
export function schaalAfmeting(breedte, hoogte, max = MAX_ZIJDE) {
  const b = Math.max(1, Math.round(Number(breedte) || 1));
  const h = Math.max(1, Math.round(Number(hoogte) || 1));
  const langste = Math.max(b, h);
  if (langste <= max) return { breedte: b, hoogte: h, geschaald: false };
  const f = max / langste;
  return { breedte: Math.max(1, Math.round(b * f)), hoogte: Math.max(1, Math.round(h * f)), geschaald: true };
}

// Grootte in bytes van de base64-inhoud van een data-URL.
export function dataUrlBytes(dataUrl) {
  const s = String(dataUrl || '');
  const i = s.indexOf(',');
  if (i < 0) return 0;
  const b64 = s.slice(i + 1).replace(/\s+/g, '');
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.max(0, Math.floor((b64.length * 3) / 4) - padding);
}

// Alle afbeeldingsbestanden uit een paste- of drop-event, in volgorde.
export function afbeeldingenUitEvent(e) {
  const dt = (e && (e.clipboardData || e.dataTransfer)) || null;
  if (!dt) return [];
  const uit = [];
  const items = dt.items ? Array.from(dt.items) : [];
  for (const it of items) {
    if (it.kind === 'file' && /^image\//i.test(it.type || '')) { const f = it.getAsFile(); if (f) uit.push(f); }
  }
  if (!uit.length && dt.files) {
    for (const f of Array.from(dt.files)) if (/^image\//i.test(f.type || '')) uit.push(f);
  }
  return uit;
}

// Totaal aan geplakte afbeeldingen (bytes) dat al in een stuk HTML zit.
export function inlineBytesIn(html) {
  let totaal = 0;
  const re = /src\s*=\s*["']data:image\/[^"']*["']/gi;
  let m;
  while ((m = re.exec(String(html || '')))) totaal += dataUrlBytes(m[0].slice(m[0].indexOf('data:')));
  return totaal;
}

const laadBeeld = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Afbeelding kon niet gelezen worden')); };
  img.src = url;
});
const blobNaarDataUrl = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = () => reject(r.error || new Error('Afbeelding kon niet gelezen worden'));
  r.readAsDataURL(blob);
});

// Bestand → data-URL, verkleind waar nodig. Geeft { dataUrl, breedte, hoogte, bytes }.
export async function verkleinAfbeelding(file, { maxZijde = MAX_ZIJDE, pngGrens = PNG_GRENS } = {}) {
  const type = String((file && file.type) || '').toLowerCase();
  // GIF (animatie) en SVG niet door het canvas halen: dat maakt er een stilstaand
  // of leeg beeld van. Klein genoeg → ongewijzigd door.
  if (type === 'image/gif' || type === 'image/svg+xml') {
    const dataUrl = await blobNaarDataUrl(file);
    return { dataUrl, breedte: null, hoogte: null, bytes: file.size };
  }
  const img = await laadBeeld(file);
  const { breedte, hoogte, geschaald } = schaalAfmeting(img.naturalWidth || img.width, img.naturalHeight || img.height, maxZijde);
  // Al klein en licht genoeg: niet hercoderen (kwaliteitsverlies vermijden).
  if (!geschaald && file.size <= pngGrens && (type === 'image/png' || type === 'image/jpeg' || type === 'image/webp')) {
    return { dataUrl: await blobNaarDataUrl(file), breedte, hoogte, bytes: file.size };
  }
  const canvas = document.createElement('canvas');
  canvas.width = breedte; canvas.height = hoogte;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, breedte, hoogte);
  // JPEG-bron blijft JPEG; anders eerst PNG proberen en bij een zwaar resultaat
  // (foto, verloop) alsnog JPEG. Een JPEG krijgt een witte achtergrond.
  let dataUrl = type === 'image/jpeg' ? '' : canvas.toDataURL('image/png');
  if (!dataUrl || dataUrlBytes(dataUrl) > pngGrens) {
    const c2 = document.createElement('canvas');
    c2.width = breedte; c2.height = hoogte;
    const x = c2.getContext('2d');
    x.fillStyle = '#ffffff'; x.fillRect(0, 0, breedte, hoogte);
    x.drawImage(img, 0, 0, breedte, hoogte);
    dataUrl = c2.toDataURL('image/jpeg', JPEG_KWALITEIT);
  }
  return { dataUrl, breedte, hoogte, bytes: dataUrlBytes(dataUrl) };
}

// De HTML die in de editor komt. De breedte gaat als attribuut mee (Outlook
// negeert max-width): hooguit MAIL_BEELD_BREEDTE — de mailkolom is 600 px met
// 28 px marge aan weerszijden (config/email.js), dus 540 px past zonder dat de
// tabel breder wordt dan de kolom.
export const MAIL_BEELD_BREEDTE = 540;
export const afbeeldingHtml = ({ dataUrl, breedte }) =>
  `<img src="${dataUrl}" alt="" style="max-width:100%;height:auto;display:block;margin:8px 0;"${breedte ? ` width="${Math.min(breedte, MAIL_BEELD_BREEDTE)}"` : ''}>`;
